/**
 * SavedListView — dedicated /projects/lists/:id page.
 *
 * Renders inside the standard AppShell so the sidebar + header stay
 * visible (unlike the compact popup preview that only shows five
 * columns). Mirrors the DatabaseDetail Data-tab styling exactly so the
 * saved list feels like a filtered view of the underlying database:
 *
 *   • Dark-navy sortable header row (`bg-shell-side` / `text-white/90`)
 *   • Chevron per row → expands an inline "Full record" accordion with
 *     every field, not just the top-five columns
 *   • Search + pagination + record count in the header strip
 *   • "Open in database" CTA deep-links to the source database on the
 *     Data tab (with the single row highlighted when there's only one)
 *
 * We deliberately DON'T share the DatabaseDetail component — that page
 * does far more (buyer windows, filters, admin edit, star-sync, etc.)
 * and re-hosting it here would inherit gates we don't want on a saved
 * snapshot. This view is read-only.
 */
import React, { useEffect, useMemo, useState } from "react";
import { Link, useParams } from "react-router-dom";
import AppShell from "../components/AppShell";
import PageHero from "../components/PageHero";
import SidebarToggleButton from "../components/SidebarToggleButton";
import { api } from "../lib/api";
import {
  ArrowLeft, ChevronDown, ChevronRight, ChevronLeft,
  ExternalLink, Loader2, Search, Table as TableIcon,
} from "lucide-react";

const PAGE_SIZE = 50;

export default function SavedListView() {
  const { id } = useParams();
  const [list, setList] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(false);
  const [query, setQuery] = useState("");
  const [page, setPage] = useState(1);
  const [expandedRows, setExpandedRows] = useState(() => new Set());
  const [dbName, setDbName] = useState("");

  useEffect(() => {
    setLoading(true);
    api.get(`/lists/${id}`)
      .then((r) => setList(r.data))
      .catch(() => setError(true))
      .finally(() => setLoading(false));
  }, [id]);

  // Resolve the human-readable database name so the header can say
  // "Product Development · 2 records" instead of the raw slug.
  useEffect(() => {
    if (!list?.database_slug) return;
    api.get("/databases")
      .then((r) => {
        const match = (r.data || []).find((d) => d.slug === list.database_slug);
        if (match) setDbName(match.name || match.slug);
      })
      .catch(() => { /* fall back to the slug in the header */ });
  }, [list?.database_slug]);

  const rows = list?.rows || [];
  const columns = useMemo(() => (
    rows.length
      ? Array.from(new Set(rows.flatMap((r) => Object.keys(r).filter((k) => k !== "id" && k !== "database_slug"))))
      : []
  ), [rows]);

  const filtered = useMemo(() => {
    const q = (query || "").trim().toLowerCase();
    if (!q) return rows;
    return rows.filter((r) => Object.values(r).some((v) => String(v ?? "").toLowerCase().includes(q)));
  }, [rows, query]);

  useEffect(() => { setPage(1); }, [query, id]);
  const totalPages = Math.max(1, Math.ceil(filtered.length / PAGE_SIZE));
  const pageStart = (page - 1) * PAGE_SIZE;
  const pageEnd = pageStart + PAGE_SIZE;

  const toggleRowExpanded = (rid) => {
    setExpandedRows((prev) => {
      const next = new Set(prev);
      if (next.has(rid)) next.delete(rid);
      else next.add(rid);
      return next;
    });
  };

  const rowIds = list?.row_ids || [];
  const openHref = (() => {
    const slug = list?.database_slug || "";
    if (!slug) return "/databases";
    if (rowIds.length === 1) return `/databases/${slug}?highlight=${encodeURIComponent(rowIds[0])}`;
    return `/databases/${slug}?tab=data`;
  })();

  const missingCount = Math.max(0, rowIds.length - rows.length);

  return (
    <AppShell title={list?.name || "Saved list"}>
      <div className="w-full">
        <Link
          to="/projects"
          className="inline-flex items-center gap-1 text-xs text-muted-foreground hover:text-foreground"
          data-testid="saved-list-back"
        >
          <ArrowLeft className="w-3.5 h-3.5" /> Back to Projects
        </Link>

        <PageHero
          eyebrow="Saved list"
          icon={TableIcon}
          title={<>{list?.name || "Saved list"}</>}
          subtitle={
            list
              ? `${dbName || list.database_slug} · ${rowIds.length} record${rowIds.length === 1 ? "" : "s"}`
              : "Loading saved list…"
          }
          right={<SidebarToggleButton testId="saved-list-sidebar-toggle" />}
          className="mt-2"
          testid="saved-list-hero"
        />

        {loading ? (
          <div className="py-24 text-center text-sm text-muted-foreground inline-flex items-center gap-2 justify-center w-full">
            <Loader2 className="w-4 h-4 animate-spin" /> Loading records…
          </div>
        ) : error || !list ? (
          <div className="py-16 text-center text-sm text-muted-foreground">
            This saved list is unavailable — it may have been deleted.
          </div>
        ) : (
          <>
            <div className="mt-6 flex flex-wrap items-center justify-between gap-3">
              <div className="relative w-full sm:w-80">
                <Search className="w-4 h-4 absolute left-3 top-1/2 -translate-y-1/2 text-muted-foreground" />
                <input
                  type="search"
                  value={query}
                  onChange={(e) => setQuery(e.target.value)}
                  placeholder="Search this list…"
                  data-testid="saved-list-search"
                  className="w-full pl-9 pr-3 py-2 rounded-full border border-shell-border bg-white text-sm outline-none focus:ring-2 focus:ring-primary/30"
                />
              </div>
              <div className="flex items-center gap-3 text-xs text-muted-foreground">
                <span data-testid="saved-list-count">
                  {filtered.length} of {rows.length}
                </span>
                <Link
                  to={openHref}
                  data-testid="saved-list-open-in-database"
                  className="inline-flex items-center gap-1 text-xs font-semibold border border-shell-border rounded-full px-3 py-1.5 hover:bg-muted"
                >
                  <ExternalLink className="w-3 h-3" /> Open in database
                </Link>
              </div>
            </div>

            {missingCount > 0 && (
              <p
                className="mt-3 text-[11px] font-semibold text-pink-800 bg-pink-50 border border-pink-200 rounded-lg px-3 py-2"
                data-testid="saved-list-missing-warning"
              >
                {missingCount} record{missingCount === 1 ? " has" : "s have"} been removed from the database since this list was saved.
              </p>
            )}

            {rows.length === 0 ? (
              <p className="mt-8 text-sm text-muted-foreground text-center py-16">
                This saved list is empty.
              </p>
            ) : (
              <div className="mt-4 rounded-2xl border border-shell-border overflow-hidden bg-white">
                <div className="overflow-x-auto">
                  <table className="w-full text-sm" data-testid="saved-list-table">
                    <thead>
                      <tr>
                        <th className="w-10 px-4 py-3 bg-shell-side"></th>
                        {columns.map((c) => (
                          <th
                            key={c}
                            className="px-4 py-3 font-semibold uppercase text-[11px] tracking-widest whitespace-nowrap bg-shell-side text-white/90 text-left"
                          >
                            {c.replace(/_/g, " ")}
                          </th>
                        ))}
                      </tr>
                    </thead>
                    <tbody>
                      {filtered.slice(pageStart, pageEnd).map((r, i) => {
                        const rid = r.id || String(pageStart + i);
                        const expanded = expandedRows.has(rid);
                        return (
                          <React.Fragment key={rid}>
                            <tr
                              data-testid={`saved-list-row-${rid}`}
                              className={[
                                "border-t border-shell-border hover:bg-muted/40 transition-colors",
                                expanded ? "bg-primary/[0.03]" : "",
                              ].join(" ")}
                            >
                              <td className="px-4 py-3 align-top">
                                <button
                                  type="button"
                                  onClick={() => toggleRowExpanded(rid)}
                                  data-testid={`saved-list-expand-${rid}`}
                                  title={expanded ? "Hide full details" : "Show full details"}
                                  className="p-1 rounded hover:bg-muted text-muted-foreground hover:text-primary transition-colors"
                                >
                                  {expanded ? <ChevronDown className="w-4 h-4" /> : <ChevronRight className="w-4 h-4" />}
                                </button>
                              </td>
                              {columns.map((c) => (
                                <td key={c} className="px-4 py-3 align-top max-w-[260px]">
                                  <Cell value={r[c]} />
                                </td>
                              ))}
                            </tr>
                            {expanded && (
                              <tr
                                data-testid={`saved-list-row-detail-${rid}`}
                                className="bg-muted/30 border-t border-shell-border"
                              >
                                <td></td>
                                <td colSpan={columns.length} className="px-4 py-5">
                                  <div className="rounded-xl border border-shell-border bg-white p-5 shadow-inner">
                                    <p className="text-[10px] uppercase tracking-widest text-shell-accent font-semibold mb-3">
                                      Full record
                                    </p>
                                    <div className="grid sm:grid-cols-2 lg:grid-cols-3 gap-4">
                                      {columns.map((k) => (
                                        <div key={k} className="min-w-0">
                                          <p className="text-[10px] uppercase tracking-widest text-muted-foreground font-semibold">
                                            {k.replace(/_/g, " ")}
                                          </p>
                                          <div className="mt-1 text-sm text-foreground break-words whitespace-pre-wrap">
                                            <CellFull value={r[k]} />
                                          </div>
                                        </div>
                                      ))}
                                    </div>
                                  </div>
                                </td>
                              </tr>
                            )}
                          </React.Fragment>
                        );
                      })}
                    </tbody>
                  </table>
                </div>
              </div>
            )}

            {filtered.length > PAGE_SIZE && (
              <div
                className="mt-4 mb-12 flex items-center justify-between gap-4 rounded-xl border border-shell-border bg-white/95 px-4 py-3 shadow-sm text-xs text-muted-foreground"
                data-testid="saved-list-pagination"
              >
                <button
                  type="button"
                  onClick={() => setPage((p) => Math.max(1, p - 1))}
                  disabled={page === 1}
                  data-testid="saved-list-page-prev"
                  className="inline-flex items-center gap-1.5 rounded-full border border-primary/40 bg-white px-5 py-2 text-sm font-semibold text-primary hover:bg-primary hover:text-primary-foreground disabled:opacity-40 disabled:cursor-not-allowed"
                >
                  <ChevronLeft className="w-4 h-4" /> Prev
                </button>
                <span data-testid="saved-list-page-label" className="tabular-nums text-center text-xs sm:text-sm">
                  Page <b className="text-foreground">{page}</b> of{" "}
                  <b className="text-foreground">{totalPages}</b> · showing{" "}
                  {pageStart + 1}–{Math.min(pageEnd, filtered.length)} of{" "}
                  {filtered.length.toLocaleString()}
                </span>
                <button
                  type="button"
                  onClick={() => setPage((p) => Math.min(totalPages, p + 1))}
                  disabled={page === totalPages}
                  data-testid="saved-list-page-next"
                  className="inline-flex items-center gap-1.5 rounded-full border border-primary/40 bg-white px-5 py-2 text-sm font-semibold text-primary hover:bg-primary hover:text-primary-foreground disabled:opacity-40 disabled:cursor-not-allowed"
                >
                  Next <ChevronRight className="w-4 h-4" />
                </button>
              </div>
            )}

            {list?.comments && (
              <p className="mt-6 text-sm text-muted-foreground" data-testid="saved-list-comments">
                {list.comments}
              </p>
            )}
          </>
        )}
      </div>
    </AppShell>
  );
}

function Cell({ value }) {
  if (value == null || value === "") return <span className="text-muted-foreground">—</span>;
  if (Array.isArray(value)) return <span className="break-words">{value.join(", ")}</span>;
  if (typeof value === "object") return <span className="break-words">{JSON.stringify(value)}</span>;
  const s = String(value);
  if (/^https?:\/\//i.test(s)) {
    return (
      <a
        href={s}
        target="_blank"
        rel="noreferrer"
        className="text-primary hover:underline break-all inline-flex items-center gap-1"
        title={s}
      >
        {s.replace(/^https?:\/\//, "").replace(/\/$/, "").slice(0, 40)}
        <ExternalLink className="w-3 h-3 opacity-60" />
      </a>
    );
  }
  if (s.length > 120) {
    return <span className="line-clamp-2 text-foreground/90" title={s}>{s}</span>;
  }
  return <span className="break-words">{s}</span>;
}

function CellFull({ value }) {
  if (value == null || value === "") return <span className="text-muted-foreground">—</span>;
  if (Array.isArray(value)) return <span className="break-words whitespace-pre-wrap">{value.join(", ")}</span>;
  if (typeof value === "object") return <span className="break-words whitespace-pre-wrap">{JSON.stringify(value, null, 2)}</span>;
  const s = String(value);
  if (/^https?:\/\//i.test(s)) {
    return (
      <a
        href={s}
        target="_blank"
        rel="noreferrer"
        className="text-primary hover:underline break-all inline-flex items-center gap-1"
      >
        {s.replace(/^https?:\/\//, "").replace(/\/$/, "")}
        <ExternalLink className="w-3 h-3 opacity-60 shrink-0" />
      </a>
    );
  }
  return <span className="break-words whitespace-pre-wrap">{s}</span>;
}
